/**
 * Form draft utilities for persisting form data to localStorage
 * with Date serialization support and TTL expiration
 */

const DATE_MARKER = '__DATE__:'

/**
 * Storage structure for form drafts
 */
interface StoredDraft<T> {
  data: T
  timestamp: number
}

/**
 * Custom JSON replacer to serialize Date objects
 */
function dateReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Date) {
    return `${DATE_MARKER}${value.toISOString()}`
  }
  return value
}

/**
 * Custom JSON reviver to deserialize Date objects
 */
function dateReviver(_key: string, value: unknown): unknown {
  if (typeof value === 'string' && value.startsWith(DATE_MARKER)) {
    const dateString = value.substring(DATE_MARKER.length)
    return new Date(dateString)
  }
  return value
}

/**
 * Check if localStorage is available
 */
function isStorageAvailable(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false
  }

  try {
    const testKey = '__storage_test__'
    localStorage.setItem(testKey, 'test')
    localStorage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}

/**
 * Save form draft to localStorage with timestamp
 * TTL is enforced during load, not save
 *
 * @param key - Storage key (should follow 'votebox_' prefix convention)
 * @param data - Form data to save
 */
export function saveFormDraft<T>(key: string, data: T): void {
  if (!isStorageAvailable()) {
    return
  }

  try {
    const draft: StoredDraft<T> = {
      data,
      timestamp: Date.now(),
    }

    const json = JSON.stringify(draft, dateReplacer)
    localStorage.setItem(key, json)
  } catch (error) {
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      console.warn('Form draft: Storage quota exceeded, draft not saved')
      return
    }
    console.error(`Form draft: Failed to save draft: ${error}`)
  }
}

/**
 * Load form draft from localStorage with expiration check
 *
 * @param key - Storage key
 * @param ttlMs - Optional time-to-live in milliseconds (default: 7 days)
 * @returns Form data or null if not found/expired
 */
export function loadFormDraft<T>(key: string, ttlMs?: number): T | null {
  if (!isStorageAvailable()) {
    return null
  }

  try {
    const stored = localStorage.getItem(key)
    if (!stored) {
      return null
    }

    const draft: StoredDraft<T> = JSON.parse(stored, dateReviver)

    // Check if draft is expired
    if (ttlMs && Date.now() - draft.timestamp > ttlMs) {
      removeFormDraft(key)
      return null
    }

    return draft.data
  } catch (error) {
    // Remove corrupted data
    console.warn('Form draft: Corrupted data removed', error)
    removeFormDraft(key)
    return null
  }
}

/**
 * Remove form draft from localStorage
 *
 * @param key - Storage key
 */
export function removeFormDraft(key: string): void {
  if (!isStorageAvailable()) {
    return
  }

  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error(`Form draft: Failed to remove draft: ${error}`)
  }
}

/**
 * Check if a draft exists in localStorage
 *
 * @param key - Storage key
 * @returns True if draft exists
 */
export function hasDraft(key: string): boolean {
  if (!isStorageAvailable()) {
    return false
  }

  try {
    return localStorage.getItem(key) !== null
  } catch {
    return false
  }
}
